<template>
  <div
    ref="moduleRef"
    class="video-testimonials"
  >
    <div class="video-testimonials__container">
      <div class="video-testimonials__container__header">
        <h2 class="video-testimonials__container__header--title">
          {{ t('b2b__landing_page__video_testimonials__title') }}
        </h2>
      </div>
      <div class="video-testimonials__container__cards">
        <div
          v-for="(
            { title, author, src, playing, loading, hiddenOnMobile }, index
          ) in cards"
          :key="index"
          class="video-testimonials__container__cards--card"
          :class="{ 'hidden-on-mobile': hiddenOnMobile }"
        >
          <video
            ref="videos"
            :src="src + '#t=0.01'"
            playsinline
            :aria-label="`Video testimonial by ${author}: ${title}`"
            @click="handlePlayPause(index)"
            @pause="pauseVideo(index)"
            @play="playVideo(index)"
            @ended="handleVideoEnded(index)"
            @waiting="handleWaiting(index)"
            @canplay="handleCanPlay(index)"
          />
          <div
            v-show="playing"
            class="controls"
          >
            <BaseButton
              icon
              inverted
              class="stop-button"
              aria-label="Pause video"
              @click="handlePlayPause(index)"
            >
              <KDSSpinner v-show="loading" />
              <PauseIcon v-show="!loading" />
            </BaseButton>
          </div>
          <div
            v-show="!playing"
            class="overlay"
            @click="handlePlayPause(index)"
          >
            <BaseButton
              icon
              inverted
              aria-label="Play video"
              class="play-button"
            >
              <PlayIcon />
            </BaseButton>
            <p
              class="kds-typography-title-medium kds-color-text-on-interactive-strong"
            >
              {{ title }}
            </p>
            <p
              v-if="author"
              class="kds-typography-title-large kds-color-text-on-interactive-strong"
            >
              {{ author }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, useTemplateRef } from 'vue'
import { useI18n } from 'petite-vue-i18n'
import testimonial1 from '@us/app/assets/videos/Nora_Testimonial.mp4'
import testimonial2 from '@us/app/assets/videos/Nameless_Testimonial.mp4'
import testimonial3 from '@us/app/assets/videos/Devin_Testimonial.mp4'
import BaseButton from '@shared/components/BaseButton.vue'
import PlayIcon from '@shared/assets/icon/PlayIcon.vue'
import PauseIcon from '@shared/assets/icon/PauseIcon.vue'
import KDSSpinner from '@shared/components/KDSSpinner.vue'

const { t } = useI18n()
const moduleRef = ref(null)
const videoRefs = useTemplateRef('videos')

const cards = ref([
  {
    title: t('b2b__landing_page__video_testimonials__card_one__title'),
    author: t('b2b__landing_page__video_testimonials__card_one__author'),
    src: testimonial1,
    playing: false,
    loading: false,
    hiddenOnMobile: false,
  },
  {
    title: t('b2b__landing_page__video_testimonials__card_two__title'),
    src: testimonial2,
    playing: false,
    loading: false,
    hiddenOnMobile: false,
  },
  {
    title: t('b2b__landing_page__video_testimonials__card_three__title'),
    author: t('b2b__landing_page__video_testimonials__card_three__author'),
    src: testimonial3,
    playing: false,
    loading: false,
    hiddenOnMobile: true,
  },
])

const pauseVideo = (index) => {
  const video = videoRefs.value[index]
  video.pause()
  video.currentTime = 0
  cards.value[index].playing = false
}

const playVideo = (index) => {
  videoRefs.value[index].play()
  cards.value[index].playing = true
}

const pauseAllVideos = () => {
  videoRefs.value.forEach((video, index) => {
    if (!video.paused) {
      pauseVideo(index)
    }
  })
}

const handlePlayPause = (index) => {
  const video = videoRefs.value[index]
  if (video.paused) {
    pauseAllVideos()
    playVideo(index)
  } else {
    pauseVideo(index)
  }
}

const handleVideoEnded = (index) => {
  pauseVideo(index)

  const nextIndex = index + 1
  if (nextIndex < videoRefs.value.length) {
    playVideo(nextIndex)
  }
}

const handleWaiting = (index) => {
  cards.value[index].loading = true
}

const handleCanPlay = (index) => {
  cards.value[index].loading = false
}

let observer = null

onMounted(() => {
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          // Module is out of view, pause all videos
          pauseAllVideos()
        }
      })
    },
    {
      threshold: 0, // Fires when element becomes fully out of view
      rootMargin: '0px',
    },
  )

  if (moduleRef.value) {
    observer.observe(moduleRef.value)
  }
})

onUnmounted(() => {
  if (observer) {
    observer.disconnect()
  }
})
</script>

<style lang="scss" scoped>
.video-testimonials {
  padding: 40px 10px;

  @include media-up(lg) {
    padding: 96px 10px;
  }

  &__container {
    @extend %container;
    margin: auto;

    &__header {
      @include flex-container(column, center, md, center);
      @include margin(xxl, bottom);

      &--title {
        @include kds-typography-display-small;
        text-align: center;

        @include media-up(md) {
          @include kds-typography-display-medium;
        }
      }
    }

    &__cards {
      display: grid;
      gap: 32px;
      grid-template-columns: 1fr;

      @include media-up(lg) {
        grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
      }

      &--card {
        position: relative;

        &.hidden-on-mobile {
          display: none;

          @include media-up(lg) {
            display: block;
          }
        }

        video {
          display: block;
          width: 100%;
          border-radius: 16px;
          cursor: pointer;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
        }

        .controls {
          position: absolute;
          right: 16px;
          bottom: 16px;

          .stop-button {
            transition: transform 0.2s ease;
          }
        }

        .overlay {
          cursor: pointer;
          position: absolute;
          border-radius: 16px;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: start;
          justify-content: end;
          background-color: $kds-color-icon-on-interactive-disabled;
          gap: 16px;
          padding: 16px;
          box-sizing: border-box;
          user-select: none;
          -webkit-tap-highlight-color: transparent;

          .play-button {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            transition: transform 0.2s ease;
          }
        }

        &:hover {
          .stop-button {
            transform: scale(1.1);
          }

          .play-button {
            transform: translate(-50%, -50%) scale(1.1);
          }
        }
      }
    }
  }
}
</style>
